import { useCallback, useEffect, useMemo, useRef } from "react";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { fetchData } from "utils";
import type { FetchError } from "utils/fetchData/types";
import type { ResultOf, VariablesOf } from "gql.tada";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useFetch = <Query extends TypedDocumentNode<any, any>>({
  query,
  variables,
  pause = false,
  timeout = 60000,
  onSuccess,
  onError,
  setIsLoading,
}: {
  query: Query;
  variables?: VariablesOf<Query>;
  pause?: boolean;
  timeout?: number;
  onSuccess: (data: ResultOf<Query>) => void;
  onError: (error: FetchError) => void;
  setIsLoading?: (isLoading: boolean) => void;
}) => {
  // Stable serialization of variables - prevents unnecessary refetches
  const variablesKey = useMemo(() => JSON.stringify(variables), [variables]);

  // Track active AbortController for cleanup
  const controllerRef = useRef<AbortController | null>(null);

  const exeFetch = useCallback(
    (variablesParam?: VariablesOf<Query>) => {
      // Create new controller for this fetch
      const controller = new AbortController();
      controllerRef.current = controller;

      fetchData({
        query,
        variables: variablesParam || variables,
        signal: controller.signal,
        onSuccess,
        onError,
        setIsLoading,
        timeout,
      });

      return {
        cleanup: () => {
          controller.abort(new Error("Request cancelled by caller"));
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, variablesKey, timeout],
  );

  useEffect(() => {
    if (pause) {
      return;
    }
    const { cleanup } = exeFetch();
    // Cleanup on unmount: abort controller and cancel pending timeouts
    return () => cleanup();
  }, [pause, exeFetch]);

  return exeFetch;
};
